import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Upload, Storage, AutoAwesome } from "@mui/icons-material";
import HeaderNew from "../../../../../common/components/custom/header-new/HeaderNew";
import HubHeader from "../../../../../common/components/custom/hub-header/HubHeader";
import TagEnrichmentPhase from "./TagEnrichmentPhase";
import { MainRoutes } from "../../../../../common/config";
import { TagDefinition } from "../../../data/models/configuration-hub.model";
import { useGetTagEnrichmentDetailsQuery } from "../../../data/api/queries/tagLibrary";
import { ToastMessageUtil } from "../../../../../common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "../../../../../common/enums/controls/toast.enum";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import { Loading } from "modules/_shared/pages";
import "../create-tag-library-config/CreateTagLibraryConfig.scss";

type Phase = "setup" | "enrich" | "embed" | "complete";

interface LocationState {
  tags?: TagDefinition[];
  configName?: string;
  categoryName?: string;
  categoryDescription?: string;
  fromSetup?: boolean;
  masterObjectId?: number;
}

const TagEnrichmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;

  // Current phase is always "enrich" for this dedicated page
  const currentPhase: Phase = "enrich";

  // State management
  const [tags, setTags] = useState<TagDefinition[]>([]);
  const [configName, setConfigName] = useState<string>("");
  const [categoryName, setCategoryName] = useState<string>("");
  const [categoryDescription, setCategoryDescription] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [masterObjectId, setMasterObjectId] = useState<number | null>(null);

  // API hook - only call when we have a masterObjectId
  const {
    data: tagEnrichmentData,
    error: tagEnrichmentError,
    isLoading: tagEnrichmentLoading,
    refetch: refetchTagEnrichment
  } = useGetTagEnrichmentDetailsQuery(
    { CFIHOSMasterObjectId: masterObjectId!, isActive: true },
    {
      skip: !masterObjectId,
      refetchOnMountOrArgChange: true
    }
  );

  // Helper function to map API response to TagDefinition
  const mapApiResponseToTags = (apiTags: any[]): TagDefinition[] => {
    return apiTags.map((tag, index) => ({
      id: tag.id?.toString() || (index + 1).toString(),
      guidCode: tag.guidCode || "",
      tagName: tag.name || "",
      parentTagName: tag.parentTagName || "",
      tagDefinition:
        tag.isAI === "True"
          ? tag.enrichedDefinition || ""
          : tag.description || "",
      cfihosTagId: tag.cfihosId || ""
    }));
  };

  // Initialize state from navigation or localStorage
  useEffect(() => {
    if (locationState?.fromSetup && locationState?.masterObjectId) {
      // Coming from setup phase with masterObjectId
      setMasterObjectId(locationState.masterObjectId);
      setConfigName(locationState.configName || "");
      setCategoryName(locationState.categoryName || "");
      setCategoryDescription(locationState.categoryDescription || "");

      // If there are tags from setup, use them temporarily until API loads
      if (locationState.tags) {
        setTags(locationState.tags);
      }
    } else {
      // Try to restore from localStorage
      try {
        const savedState = localStorage.getItem("tagEnrichmentState");
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          setTags(parsedState.tags || []);
          setConfigName(parsedState.configName || "");
          setCategoryName(parsedState.categoryName || "");
          setCategoryDescription(parsedState.categoryDescription || "");
          setMasterObjectId(parsedState.masterObjectId || null);
        }
      } catch (error) {
        console.error("Error parsing saved state:", error);
        // If no saved state and no masterObjectId, show error or redirect
        if (!masterObjectId) {
          ToastMessageUtil.show(
            "No tag library configuration found. Please upload a tag library first.",
            ToastVariant.WARNING
          );
          navigate(MainRoutes.CREATE_TAG_LIBRARY_CONFIG);
        }
      }
    }
  }, [locationState, navigate, masterObjectId]);

  // Handle API response
  useEffect(() => {
    if (tagEnrichmentData?.success && tagEnrichmentData?.tagEnrichmentDetails) {
      const mappedTags = mapApiResponseToTags(
        tagEnrichmentData.tagEnrichmentDetails
      );
      setTags(mappedTags);

      // Save to localStorage for persistence
      const stateToSave = {
        tags: mappedTags,
        configName,
        categoryName,
        categoryDescription,
        masterObjectId
      };
      localStorage.setItem("tagEnrichmentState", JSON.stringify(stateToSave));
    }
  }, [
    tagEnrichmentData,
    configName,
    categoryName,
    categoryDescription,
    masterObjectId
  ]);

  // Handle API error
  useEffect(() => {
    if (tagEnrichmentError) {
      console.error(
        "Error fetching tag enrichment details:",
        tagEnrichmentError
      );
      ToastMessageUtil.show(
        "Failed to load tag enrichment details. Please try again.",
        ToastVariant.ERROR
      );
    }
  }, [tagEnrichmentError]);

  const handleProcessEmbedding = () => {
    if (tags.length === 0) {
      ToastMessageUtil.show(
        "No tags available to process",
        ToastVariant.WARNING
      );
      return;
    }

    // Navigate to embedding phase (could be a new page or back to the create flow)
    setIsProcessing(true);
    setProgress(0);

    // Simulate processing
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsProcessing(false);

          // Navigate to embed phase with data
          navigate(MainRoutes.CREATE_TAG_LIBRARY_CONFIG, {
            state: {
              tags,
              configName,
              categoryName,
              categoryDescription,
              phase: "embed"
            }
          });

          return 100;
        }
        return prev + 8;
      });
    }, 150);
  };

  const handlePrevious = () => {
    // Navigate back to setup phase
    navigate(MainRoutes.CREATE_TAG_LIBRARY_CONFIG, {
      state: {
        tags,
        configName,
        categoryName,
        categoryDescription,
        phase: "setup"
      }
    });
  };

  const renderProgressTracker = () => {
    const phases = [
      { key: "setup", label: "Setup", icon: <Upload /> },
      { key: "enrich", label: "Enrich", icon: <AutoAwesome /> },
      { key: "complete", label: "Complete", icon: <DoneOutlinedIcon /> }
    ];

    const getPhaseIndex = (phase: string) =>
      phases.findIndex((p) => p.key === phase);
    const currentIndex = getPhaseIndex(currentPhase);

    const isPhaseActive = (phaseKey: string) => currentPhase === phaseKey;
    const isPhaseCompleted = (phaseKey: string) => {
      const phaseIndex = getPhaseIndex(phaseKey);
      return phaseIndex < currentIndex;
    };

    return (
      <div className="progress-tracker">
        <div className="progress-steps">
          {phases.map((phase, index) => {
            const completed = isPhaseCompleted(phase.key);
            const active = isPhaseActive(phase.key);

            // Special case: when current phase is enrich and this is setup phase,
            // add enrich-active class for yellow progress line
            const enrichActive =
              currentPhase === "enrich" && phase.key === "setup";

            return (
              <div
                key={phase.key}
                className={`progress-step ${completed ? "completed" : ""} ${
                  enrichActive ? "enrich-active" : ""
                }`}
              >
                <div
                  className={`step-icon ${
                    active || completed ? "active" : "inactive"
                  }`}
                >
                  {phase.icon}
                </div>
                <div className="step-label">{phase.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getBackRoute = () => {
    if (locationState?.fromSetup && locationState?.masterObjectId) {
      return MainRoutes.TAG_LIBRARY_CONFIG;
    }
    // Otherwise, go back to create page (normal setup flow)
    return MainRoutes.CREATE_TAG_LIBRARY_CONFIG;
  };

  if (tagEnrichmentLoading) {
    return (
      <>
        <HeaderNew showSearch={false} />
        <div className="create-tag-library-container">
          <HubHeader
            className="header"
            title="Tag Library Configurations"
            description="Manage tag libraries with AI-powered enrichment and embeddings"
            showBackButton={true}
            backRoute={getBackRoute()}
          />
          <div className="container">
            {/* {renderProgressTracker()} */}
            <div className="loading-container" style={{ marginTop: "-5 rem" }}>
              <Loading />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="create-tag-library-container">
        <HubHeader
          className="header"
          title="Tag Library Configurations"
          description="Manage tag libraries with AI-powered enrichment and embeddings"
          showBackButton={true}
          backRoute={getBackRoute()}
        />
        <div className="container">
          {renderProgressTracker()}

          {/* API Loading State */}
          {/* {tagEnrichmentLoading && (
            <div className="config-card">
              <div className="progress-section">
                <div className="progress-info">
                  <span className="progress-label">
                    Loading tag enrichment details...
                  </span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill loading-shimmer" />
                </div>
              </div>
            </div>
          )} */}

          {/* Process for Embedding Progress Bar */}
          {/* {isProcessing && (
            <div className="config-card">
              <div className="progress-section">
                <div className="progress-info">
                  <span className="progress-label">
                    Processing for embedding...
                  </span>
                  <span className="progress-percent">{progress}%</span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          )} */}

          <TagEnrichmentPhase
            tags={tags}
            onTagsChange={setTags}
            onPrevious={handlePrevious}
            onNext={handleProcessEmbedding}
            onRefresh={refetchTagEnrichment}
            disabled={isProcessing || tagEnrichmentLoading}
            configName={configName}
            categoryName={categoryName}
            categoryDescription={categoryDescription}
            cfihosMasterObjectId={masterObjectId || 0}
            totalTags={tagEnrichmentData?.totalTags}
          />
        </div>
      </div>
    </>
  );
};

export default TagEnrichmentPage;
